import React from 'react';
import { DSButton, DSCard, DSIcon, DSToggle } from '../../design-system/primitives';
import { DetailRow, formatDay, formatPhone, displayNameFor, ordinal } from './helpers';
import { TIER_NAMES, TIER_LADDER, TIER_CAPABILITIES, MAX_TIER, capabilitySummary } from '../../constants/techTiers';
import { matchplayProfileUrl, ifpaProfileUrl } from '../../utils/profileUrls';
import UserAvatar from '../../components/UserAvatar';
import ProfilePhotoControl from '../../components/ProfilePhotoControl';

// Settings, credentials, and the things you are told rather than the things you
// do. The tech tier lives here, not behind a tab labelled Tech: it is a
// credential, and a Tier 0 player has to be able to meet the Game Down toggle
// without opening something that reads as "not for me".

const MEMBER_PERKS = [
  '24/7 access with your own master lock code',
  'Unlimited play, no per game credit',
  'Member only events and league nights'
];

// Read-only facts, so the value sits on the same line as its label and is right
// aligned. The editable rows stack label over value instead, because there the
// value is the thing being changed and wants the emphasis.
function MembershipRow({ label, value }) {
  return (
    <div className="mm-fact">
      <span className="mm-fact__label">{label}</span>
      <span className="mm-fact__value">{value}</span>
    </div>
  );
}

function EditButton({ onClick, label }) {
  return (
    <DSButton appearance="outline" tone="neutral" size="sm" onClick={onClick} aria-label={label}>
      Edit
    </DSButton>
  );
}

function TierLadder({ tier }) {
  return (
    <div className="mm-ladder">
      {TIER_LADDER.map((step) => (
        <span key={step} className="mm-ladder__step">
          <span
            className={`mm-ladder__bar ${
              step === tier ? 'mm-ladder__bar--current' : step < tier ? 'mm-ladder__bar--reached' : ''
            }`}
          />
          <span className="mm-ladder__label">{step}</span>
        </span>
      ))}
    </div>
  );
}

function ProfileTab({
  currentUser,
  membershipSettings,
  onboardingSteps,
  isMember,
  hasApplication,
  onEditField,
  onEditBio,
  onPhotoChange,
  onToggleGameDown,
  onApply,
  signedDocuments = [],
  onOpenDocument,
  onOpenHistory
}) {
  const tier = currentUser.tech_tier ?? 0;
  const doneCount = onboardingSteps.filter((step) => step.done).length;
  const nextStep = onboardingSteps.find((step) => !step.done);
  const bio = currentUser.host_profile;
  const needsSigning = signedDocuments.filter((d) => d.required).length;

  return (
    <>
      {/* First on the tab. It is the answer to "am I paid up", which is what a
          member opens this tab to check, and it was at the bottom under six
          sections they had to scroll past. */}
      {isMember && (
        <DSCard frame="bars" density="compact" title="Membership" headerTone="strong" flush>
          <MembershipRow label="Member since" value={formatDay(membershipSettings?.start_date) || 'Not recorded'} />
          <MembershipRow
            label="Renews on the"
            value={membershipSettings?.renewal_day_of_month
              ? `${ordinal(membershipSettings.renewal_day_of_month)} of the month`
              : 'Not set'}
          />
          <MembershipRow label="Auto renewal" value={membershipSettings?.auto_renewal ? 'On' : 'Off'} />
          <MembershipRow label="Status" value={currentUser.is_active ? 'Active' : 'Inactive'} />
        </DSCard>
      )}

      {/* What you agreed to and when. A signature is a record the member is
          party to, so they should be able to see it without asking an admin -
          and if a document is re-issued, this is where "I already signed that"
          gets checked.

          Nothing appears or disappears when a document changes: the row is
          always here and only its right-hand column changes state, so the page
          does not reflow under somebody who was reading it. */}
      {isMember && signedDocuments.length > 0 && (
        <DSCard
          frame="bars"
          density="compact"
          title="Documents you have signed"
          headerTone="strong"
          flush
          actions={needsSigning > 0
            ? <span className="mm-doc-pill">{needsSigning} needs signing</span>
            : null}
        >
          {signedDocuments.map((doc) => (
            <button
              type="button"
              key={doc.documentKey}
              className="mm-doc-row"
              onClick={() => onOpenDocument(doc.documentKey)}
            >
              <span className="mm-doc-row__main">
                <span className="mm-doc-row__title">{doc.label}</span>
                {doc.required && (
                  <span className="mm-doc-row__reason">
                    {doc.hasEverAcknowledged
                      ? `Changed ${formatDay(doc.effectiveDate) || 'recently'}`
                      : 'You have not signed this yet'}
                  </span>
                )}
              </span>
              <span className="mm-doc-row__status">
                <span className={`mm-doc-row__state ${doc.required ? 'mm-doc-row__state--due' : ''}`}>
                  {doc.required ? 'Needs signing' : `Signed ${formatDay(doc.acknowledgedAt)}`}
                </span>
                <span className="mm-doc-row__sub">
                  {doc.required
                    ? (doc.acknowledgedAt ? `You signed ${formatDay(doc.acknowledgedAt)}` : 'Never signed')
                    : 'Current'}
                </span>
              </span>
              <span className="mm-doc-row__chevron" aria-hidden="true">
                <DSIcon name="chevronRight" size="sm" />
              </span>
            </button>
          ))}

          <button type="button" className="mm-doc-history" onClick={onOpenHistory}>
            <span>Signing history</span>
            <DSIcon name="chevronRight" size="sm" />
          </button>
        </DSCard>
      )}

      {hasApplication && (
        <DSCard frame="bars" density="compact" title="Your application" className="mm-card--brand" headerTone="strong" actions={<span className="mm-aside-note">{doneCount} of {onboardingSteps.length} done</span>}>
          <div className="mm-progress" role="img" aria-label={`${doneCount} of ${onboardingSteps.length} steps done`}>
            {onboardingSteps.map((step) => (
              <span key={step.label} className={`mm-progress__seg ${step.done ? 'mm-progress__seg--done' : ''}`} />
            ))}
          </div>

          {/* No step is a button. An applicant's real job is to wait and show up,
              and a pressable-looking row that an admin has to complete in person
              only generates support requests. */}
          <ol className="mm-steps">
            {onboardingSteps.map((step, index) => (
              <li key={step.label} className="mm-steps__row">
                <span className={`mm-steps__marker ${step.done ? 'mm-steps__marker--done' : ''}`}>
                  {step.done ? <DSIcon name="check" size="sm" tone="inverse" /> : index + 1}
                </span>
                <span className="mm-steps__main">
                  <span className="mm-steps__label">{step.label}</span>
                  <span className="mm-steps__meta">{step.done ? step.doneMeta : step.todoMeta}</span>
                </span>
              </li>
            ))}
          </ol>

          <div className="mm-next">
            <p className="mm-next__title">Next: {nextStep ? nextStep.label : 'Nothing left to do'}</p>
            <p className="mm-next__body">
              {nextStep
                ? nextStep.todoMeta
                : 'Everything is done. An admin will switch your account over to full membership shortly.'}
            </p>
          </div>
          <p className="mm-note">
            Credit, your guest card, and guest passes all keep working while you wait.
          </p>
        </DSCard>
      )}

      {!isMember && !hasApplication && (
        <DSCard frame="bars" density="compact" title="Become a member" className="mm-card--brand">
          <p className="mm-lead">
            Membership is $50 a month and takes about two weeks to set up, most of it done with
            you in person.
          </p>
          <ul className="mm-perks">
            {MEMBER_PERKS.map((perk) => (
              <li key={perk} className="mm-perks__row">
                <DSIcon name="check" size="sm" tone="success" />
                <span>{perk}</span>
              </li>
            ))}
          </ul>
          <DSButton appearance="solid" tone="primary" onClick={onApply}>
            Start an application
          </DSButton>
        </DSCard>
      )}

      <DSCard frame="bars" density="compact" title="Your details" headerTone="strong" flush>
        <DetailRow
          label="Email"
          value={currentUser.email}
          action={<span className="mm-row__note">Sign in</span>}
        />
        <DetailRow
          label="First name"
          value={currentUser.first_name}
          emptyText="Not set"
          action={<EditButton label="Edit first name" onClick={() => onEditField('firstName')} />}
        />
        <DetailRow
          label="Last name"
          value={currentUser.last_name}
          emptyText="Not set"
          action={<EditButton label="Edit last name" onClick={() => onEditField('lastName')} />}
        />
        <DetailRow
          label="Preferred name"
          value={currentUser.preferred_name}
          action={<EditButton label="Edit preferred name" onClick={() => onEditField('preferredName')} />}
        />
        <DetailRow
          label="Pronouns"
          value={currentUser.pronouns}
          action={<EditButton label="Edit pronouns" onClick={() => onEditField('pronouns')} />}
        />
        <DetailRow
          label="Phone"
          value={formatPhone(currentUser.phone_number)}
          emptyText="Not set"
          action={<EditButton label="Edit phone number" onClick={() => onEditField('phoneNumber')} />}
        />
      </DSCard>

      <DSCard frame="bars" density="compact" title="Gaming accounts" subtitle="Used to enter club competitions and match your results." headerTone="strong" flush>
        <DetailRow
          label="Stern Insider"
          value={currentUser.stern_username}
          emptyText="Not linked"
          action={<EditButton label="Edit Stern Insider username" onClick={() => onEditField('sternUsername')} />}
        />
        <DetailRow
          label="MatchPlay"
          value={currentUser.matchplay_user_id}
          emptyText="Not linked"
          action={(
            <>
              {currentUser.matchplay_user_id && (
                <a
                  className="mm-linkout"
                  href={matchplayProfileUrl(currentUser.matchplay_user_id)}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Open your MatchPlay profile in a new tab"
                >
                  <DSIcon name="arrowUpRight" size="sm" />
                </a>
              )}
              <EditButton label="Edit MatchPlay user ID" onClick={() => onEditField('matchplayUserId')} />
            </>
          )}
        />
        <DetailRow
          label="IFPA"
          value={currentUser.ifpa_player_id}
          emptyText="Not linked"
          action={(
            <>
              {currentUser.ifpa_player_id && (
                <a
                  className="mm-linkout"
                  href={ifpaProfileUrl(currentUser.ifpa_player_id)}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Open your IFPA profile in a new tab"
                >
                  <DSIcon name="arrowUpRight" size="sm" />
                </a>
              )}
              <EditButton label="Edit IFPA player ID" onClick={() => onEditField('ifpaPlayerId')} />
            </>
          )}
        />
      </DSCard>

      {/* Everyone. Hosting is per event (event_hosts), so any member may end up
          hosting, and the profile photo lives here too. It used to be gated on
          the legacy role='host', which in practice hid it from everyone but
          admins. The bio is the one piece of outward-facing content anyone
          authors in this app, so it renders the way an attendee will see it
          rather than as a bare textarea. */}
      <DSCard frame="bars" density="compact" title="Host bio" subtitle="This is how attendees see you on any event you host." headerTone="strong" actions={<EditButton label="Edit host bio" onClick={onEditBio} />}>
        <div className="mm-bio-preview">
          <UserAvatar person={currentUser} className="mm-bio-preview__avatar" />
          <span className="mm-bio-preview__main">
            <span className="mm-bio-preview__by">Hosted by {displayNameFor(currentUser)}</span>
            <span className={`mm-bio-preview__text ${bio ? '' : 'mm-bio-preview__text--empty'}`}>
              {bio || 'No bio yet. If you host an event, attendees see this space on it.'}
            </span>
          </span>
        </div>
        {/* The photo shows with the bio on the hallway sign on nights you host. */}
        <div className="mm-bio-photo">
          <ProfilePhotoControl
            person={currentUser}
            onChange={() => onPhotoChange?.()}
            hint="Your photo and bio appear on the hallway sign at the club on nights you host."
          />
        </div>
      </DSCard>

      {/* Read-only by design. The tier is granted by an admin after a hands on
          check, and anything here that looked adjustable would be a support
          request. */}
      <DSCard frame="bars" density="compact" title="Tech tier" className={tier > 0 ? 'mm-card--brand' : ''}>
        {tier > 0 ? (
          <>
            <div className="mm-tier">
              <span className="mm-tier__number">{tier}</span>
              <span className="mm-tier__name">{TIER_NAMES[tier]}</span>
            </div>
            <p className="mm-note">Granted by an admin after a hands on check.</p>
          </>
        ) : (
          <>
            <p className="mm-tier__zero">Player</p>
            <p className="mm-note">
              Every member starts here. Tiers are granted by an admin after you show the work in
              person, and each one unlocks more of the machine you are allowed to open.
            </p>
          </>
        )}
        <TierLadder tier={tier} />
      </DSCard>

      <DSCard frame="bars" density="compact" title="What you may work on" subtitle={capabilitySummary(tier)} headerTone="strong" flush>
        {TIER_CAPABILITIES.map((capability) => {
          const unlocked = tier >= capability.min;
          return (
            <div key={capability.label} className={`mm-cap ${unlocked ? '' : 'mm-cap--locked'}`}>
              <span className={`mm-cap__mark ${unlocked ? 'mm-cap__mark--on' : ''}`}>
                {unlocked ? <DSIcon name="check" size="sm" tone="success" /> : null}
              </span>
              <span className="mm-cap__label">{capability.label}</span>
              {!unlocked && <span className="mm-cap__tier">Tier {capability.min}</span>}
            </div>
          );
        })}
        {tier < MAX_TIER && (
          <p className="mm-note">
            Tiers above yours are shown so the ladder stays visible, not because anything is
            missing from your account.
          </p>
        )}
      </DSCard>

      <DSCard frame="bars" density="compact" title="Alerts">
        <div className="mm-toggle-row">
          <span className="mm-toggle-row__main">
            <span className="mm-toggle-row__title">Game Down alerts</span>
            <span className="mm-toggle-row__body">
              Get a notification when a machine goes down, so you know before you drive over.
            </span>
          </span>
          <DSToggle
            id="mm-game-down"
            checked={!!currentUser.notify_on_game_down}
            onChange={onToggleGameDown}
          />
        </div>
        <p className="mm-note">
          This is the only automatic notification this feature sends, set by you, for you.
        </p>
      </DSCard>

    </>
  );
}

export default ProfileTab;
